import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteTransferOrderMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
} from '@dms/shared';
import {WAREHOUSE_PDF_DEFINITIONS, useWarehouseGridPdfExport} from '@dms/warehouse/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TransfersList() {
  const onPdfExportCallback = useWarehouseGridPdfExport(
    i18n.t('page.warehouse.labels.transfers'),
    WAREHOUSE_PDF_DEFINITIONS.transfers
  );
  const navigate = useNavigate();

  const [deleteTransferOrder] = useDeleteTransferOrderMutation();

  const actionCallback: ActionCallback = async ({
    actionKey,
    rowId,
    refreshData,
    deselectAll,
    sourceSearchParams,
  }) => {
    await match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;
        navigate(
          composePath(warehouseRoutes.transferOrderDetailOverview, {
            queryParams: sourceSearchParams,
            params: {id},
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        openDeleteDialog({
          onConfirm: () =>
            deleteTransferOrder({transferOrderId: ids})
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
              })
              .then(() => refreshData())
              .then(() => deselectAll())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };
  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseTransferOrder}
        pdfColumns={WAREHOUSE_PDF_DEFINITIONS.transfers.columns}
        onPdfExportCallback={onPdfExportCallback}
        actionCallback={actionCallback}
        data-testid={testIds.warehouse.transfers('transfers')}
      />
    </FullscreenDataGridWrapper>
  );
}
